/**
 * ParkDraftDialog — "Park this draft": why the desk is leaving it un-keyed.
 *
 * Emily's ask (2026-09-24 order portal review): a marker anyone can see that
 * the desk is aware and why, with a short note such as "waiting for the
 * customer to send over credit app". The draft stays in the drafts list as
 * her reminder; Laura's report and the critical alerts stop chasing it.
 */
import { useState } from 'react';
import { PARK_REASON_LABELS, type DraftParkReason } from '../../types/orders';
import AudioNotesInput from './AudioNotesInput';
import styles from './ParkDraftDialog.module.css';

export const PARK_NOTE_MAX = 500;

interface ParkDraftDialogProps {
  /** Customer / PO line shown under the title so the CSR knows which draft. */
  subject: string;
  /** Pre-selected when re-parking an already parked draft. */
  initialReason?: DraftParkReason | null;
  initialNote?: string | null;
  submitting?: boolean;
  error?: string | null;
  initialUntil?: string | null;
  onCancel: () => void;
  /** `until` is an ISO date (YYYY-MM-DD) or null. */
  onSubmit: (reason: DraftParkReason, note: string, until: string | null) => void;
}

function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

const REASONS: DraftParkReason[] = [
  'waiting_on_customer',
  'waiting_on_rep',
  'waiting_on_credit',
  'save_for_later',
];

export default function ParkDraftDialog({
  subject,
  initialReason = null,
  initialNote = '',
  initialUntil = null,
  submitting = false,
  error = null,
  onCancel,
  onSubmit,
}: ParkDraftDialogProps) {
  const [reason, setReason] = useState<DraftParkReason | null>(initialReason);
  const [note, setNote] = useState(initialNote ?? '');
  const [until, setUntil] = useState<string>(initialUntil ?? '');
  const tooLong = note.length > PARK_NOTE_MAX;
  const dateInPast = until !== '' && until < todayIso();
  const canSubmit = reason !== null && !tooLong && !dateInPast && !submitting;

  return (
    <div
      className={styles.overlay}
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="park-draft-title"
      data-testid="park-draft-dialog"
    >
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <h2 id="park-draft-title" className={styles.title}>
          Park this draft
        </h2>
        <p className={styles.message}>
          {subject}. It stays in your drafts as a reminder; the morning report and the
          alerts will know the desk is on it.
        </p>

        <fieldset className={styles.choices}>
          <legend className={styles.legend}>Why is it waiting?</legend>
          {REASONS.map((r) => (
            <button
              key={r}
              type="button"
              data-testid={`park-reason-${r}`}
              className={reason === r ? styles.choiceSel : styles.choice}
              onClick={() => setReason(r)}
              disabled={submitting}
            >
              {PARK_REASON_LABELS[r]}
            </button>
          ))}
        </fieldset>

        <AudioNotesInput
          value={note}
          onChange={setNote}
          disabled={submitting}
          rows={2}
          label="Short note for whoever looks at this (optional)"
          placeholder="e.g. waiting for the customer to send over the credit app"
        />
        {tooLong && (
          <span className={styles.hint} data-testid="park-note-hint">
            Keep the note under {PARK_NOTE_MAX} characters.
          </span>
        )}

        <label className={styles.dateField}>
          <span className={styles.dateLabel}>Come back to it on (optional)</span>
          <input
            type="date"
            data-testid="park-until"
            className={styles.dateInput}
            value={until}
            min={todayIso()}
            onChange={(e) => setUntil(e.target.value)}
            disabled={submitting}
          />
          <span className={styles.dateHelp}>
            The alerts on this draft stay quiet until then, and turn red again the day after.
          </span>
          {dateInPast && (
            <span className={styles.hint} data-testid="park-until-hint">
              Pick today or a later date.
            </span>
          )}
        </label>

        {error && (
          <p className={styles.error} role="alert" data-testid="park-error">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            data-testid="park-submit"
            className={styles.submit}
            onClick={() => reason && onSubmit(reason, note.trim(), until || null)}
            disabled={!canSubmit}
          >
            {submitting ? 'Parking…' : 'Park draft'}
          </button>
        </div>
      </div>
    </div>
  );
}
